/**
 * Pro tier (golf shop / fitter) entitlement helpers.
 *
 * Single source of truth for what each Pro tier unlocks. The runtime
 * gates (Add-shaft button, Customer DB cap, Stripe Connect access,
 * etc.) and the upgrade-prompt copy all read from here so server,
 * client, and the pricing matrix stay aligned.
 *
 * Tier resolution: `professionals.tier` is the persisted column.
 * D9 widened the CHECK constraint to include 'standard' and 'premium'
 * alongside the legacy 'basic' value. We normalise to one of:
 *   'trial' | 'basic' | 'standard' | 'premium'
 *
 * Anything else (including the retired 'shop' | 'fitting' |
 * 'fulfillment' values and the original 'pro') maps to 'standard' as
 * a defensive default so existing customers don't lose access while
 * the data backfill catches up.
 */

export type ProTier = 'trial' | 'basic' | 'standard' | 'premium';

const TIER_RANK: Record<ProTier, number> = {
  trial: 0,
  basic: 1,
  standard: 2,
  premium: 3,
};

/** Normalise the raw value off `professionals.tier` into a ProTier. */
export function normalizeProTier(raw: string | null | undefined): ProTier {
  const v = String(raw ?? '').toLowerCase();
  if (v === 'trial') return 'trial';
  if (v === 'basic') return 'basic';
  if (v === 'standard') return 'standard';
  if (v === 'premium') return 'premium';
  // Legacy values (pre-D9) — defensive default to 'standard' so
  // existing subscribers keep their feature access during the
  // data migration window.
  if (v === 'pro' || v === 'fitting') return 'standard';
  if (v === 'fulfillment') return 'premium';
  if (v === 'shop') return 'basic';
  return 'basic';
}

/** True when the current tier is at-or-above the required minimum. */
export function tierAtLeast(current: ProTier, minimum: ProTier): boolean {
  return TIER_RANK[current] >= TIER_RANK[minimum];
}

/* ────────────────────────────────────────────────────────────────── */
/* Feature gates — keyed by what they unlock                           */
/* ────────────────────────────────────────────────────────────────── */

export interface ProFeatureLimits {
  /** Customers in the database. -1 = unlimited. */
  customerDbCap: number;
  /** Fittings per calendar month. -1 = unlimited. */
  fittingsPerMonth: number;
  /** AI fitting briefings per calendar month. -1 = unlimited. 0 = locked. */
  briefingsPerMonth: number;
  /** Quotes per calendar month. -1 = unlimited. */
  quotesPerMonth: number;
  /** Multi-option shafts/grips on quotes (vs single recommended). */
  multiOptionQuotes: boolean;
  /** Stripe Connect access for processing payments. */
  stripeConnect: boolean;
  /** Saved customer-search views. */
  savedViews: boolean;
  /** Automated quote follow-up emails (3-touch sequence). */
  automatedFollowUps: boolean;
  /** Order management dashboard. */
  orderManagement: boolean;
  /** Promotion vouchers (Stripe coupons UI). */
  promotionVouchers: boolean;
  /** Purchase orders + supplier dispatch. */
  purchaseOrders: 'none' | 'single' | 'multi';
  /** Recovery analytics with CSV export. */
  recoveryAnalyticsExport: boolean;
  /** Revenue dashboard. */
  revenueDashboard: boolean;
  /** Equipment trend insights. */
  equipmentTrends: boolean;
  /** Phone support. */
  phoneSupport: boolean;
}

const LIMITS_BY_TIER: Record<ProTier, ProFeatureLimits> = {
  trial: {
    // Trial mirrors Standard so trialists can experience the most
    // common tier during onboarding — converts better than gating
    // them down to Basic.
    customerDbCap: 2500,
    fittingsPerMonth: 20,
    briefingsPerMonth: 20,
    quotesPerMonth: -1,
    multiOptionQuotes: false,
    stripeConnect: true,
    savedViews: false,
    automatedFollowUps: false,
    orderManagement: true,
    promotionVouchers: true,
    purchaseOrders: 'single',
    recoveryAnalyticsExport: false,
    revenueDashboard: true,
    equipmentTrends: false,
    phoneSupport: false,
  },
  basic: {
    customerDbCap: 500,
    fittingsPerMonth: 5,
    briefingsPerMonth: 0,
    quotesPerMonth: 10,
    multiOptionQuotes: false,
    stripeConnect: false,
    savedViews: false,
    automatedFollowUps: false,
    orderManagement: false,
    promotionVouchers: false,
    purchaseOrders: 'none',
    recoveryAnalyticsExport: false,
    revenueDashboard: false,
    equipmentTrends: false,
    phoneSupport: false,
  },
  standard: {
    customerDbCap: 2500,
    fittingsPerMonth: 20,
    briefingsPerMonth: 20,
    quotesPerMonth: -1,
    multiOptionQuotes: false,
    stripeConnect: true,
    savedViews: false,
    automatedFollowUps: false,
    orderManagement: true,
    promotionVouchers: true,
    purchaseOrders: 'single',
    recoveryAnalyticsExport: false,
    revenueDashboard: true,
    equipmentTrends: false,
    phoneSupport: false,
  },
  premium: {
    customerDbCap: -1,
    fittingsPerMonth: -1,
    briefingsPerMonth: -1,
    quotesPerMonth: -1,
    multiOptionQuotes: true,
    stripeConnect: true,
    savedViews: true,
    automatedFollowUps: true,
    orderManagement: true,
    promotionVouchers: true,
    purchaseOrders: 'multi',
    recoveryAnalyticsExport: true,
    revenueDashboard: true,
    equipmentTrends: true,
    phoneSupport: true,
  },
};

/** Look up the full limits matrix for the supplied tier. */
export function getProFeatureLimits(tier: ProTier): ProFeatureLimits {
  return LIMITS_BY_TIER[tier];
}

/** Friendly tier label for upgrade-prompt copy. */
export function proTierLabel(tier: ProTier): string {
  switch (tier) {
    case 'trial': return 'Trial';
    case 'basic': return 'Pro Basic';
    case 'standard': return 'Pro Standard';
    case 'premium': return 'Pro Premium';
  }
}

/** Cheapest tier that unlocks a given feature — used in upgrade prompts. */
export function minTierForFeature(
  feature:
    | 'multiOptionQuotes'
    | 'stripeConnect'
    | 'savedViews'
    | 'automatedFollowUps'
    | 'orderManagement'
    | 'promotionVouchers'
    | 'recoveryAnalyticsExport'
    | 'revenueDashboard'
    | 'equipmentTrends'
    | 'phoneSupport',
): ProTier {
  for (const tier of ['basic', 'standard', 'premium'] as const) {
    const v = LIMITS_BY_TIER[tier][feature];
    if (v === true) return tier;
  }
  return 'premium';
}
